import { useState, useEffect } from 'react';
import API_BASE_URL from '../config';
import { loadJson, reportApiError } from '../utils/apiRequest';
import toast from 'react-hot-toast';
import { aufTaste } from '../utils/tastaturBedienung';
import { buildSteps } from './analyse/analyseSchritte';
import { ProjectSummaryPanel } from './analyse/ProjectSummaryPanel';
import { DetailLabel, HeadingRow } from './analyse/analyseTeile';

// ── Pipeline-Schritte ────────────────────────────────────────────────────────

export default function AnalyseCentrale({ projectId, leadId, websiteUrl, token, onDataUpdate, stepId, confirmedSteps, onStepConfirmed }) {
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const [running, setRunning]         = useState(false);
  const [confirmingStep, setConfirmingStep] = useState(false);
  const [currentStep, setCurrentStep] = useState(-1);
  const [stepProgress, setStepProgress] = useState(0);
  const [stepResults, setStepResults]     = useState({});
  const [stepErrors, setStepErrors]       = useState({});
  const [parallelRunning, setParallelRunning] = useState(new Set());
  const [pages, setPages]             = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [hostingData, setHostingData] = useState(null);
  const [savedPagespeed, setSavedPagespeed] = useState(null);
  const [savedBrand, setSavedBrand]   = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [search, setSearch]             = useState('');
  const [sortBy, setSortBy]             = useState('url');
  const [showFullText, setShowFullText] = useState(false);

  const steps = buildSteps(projectId, leadId, websiteUrl, headers);

  // ── Gespeicherte Ergebnisse laden (nur lesen, nichts ausfuehren) ────────
  useEffect(() => {
    if (!leadId) return;
    loadSavedResults();
  }, [leadId]); // eslint-disable-line

  const loadSavedResults = async () => {
    setPagesLoading(true);
    const saved = {};
    try {
      const [contentRes, hostingRes, psRes, brandRes] = await Promise.allSettled([
        loadJson(`${API_BASE_URL}/api/crawler/content/${leadId}`, { headers }, { context: 'Website-Inhalte', fallback: [] }),
        loadJson(`${API_BASE_URL}/api/projects/${projectId}/hosting-info`, { headers }, { context: 'Hosting-Daten' }),
        loadJson(`${API_BASE_URL}/api/leads/${leadId}/pagespeed`, { headers }, { context: 'PageSpeed' }),
        loadJson(`${API_BASE_URL}/api/branddesign/${leadId}`, { headers }, { context: 'Markendesign' }), // for summary panel only
      ]);

      // Crawler + Content
      const content = contentRes.status === 'fulfilled' ? (contentRes.value || []) : [];
      if (content.length > 0) {
        setPages(content);
        if (!selectedPage) setSelectedPage(content[0]);
        saved['url-crawl'] = { urls: content.length };
        const scraped = content.filter(p => p.full_text || p.text_preview).length;
        if (scraped > 0) saved['content-scrape'] = { pages: scraped };
        // GA check from existing content
        const GA_PATTERNS = ['gtag/js', 'google-analytics', 'googletagmanager', 'ga4', 'gtm.js'];
        let gaFound = false;
        for (const page of content) {
          const text = (page.full_text || '') + (page.url || '');
          if (GA_PATTERNS.some(p => text.toLowerCase().includes(p))) { gaFound = true; break; }
        }
        saved['analytics'] = { ga_found: gaFound };
      }

      // Hosting
      const hosting = hostingRes.status === 'fulfilled' ? hostingRes.value : null;
      if (hosting?.hosting_provider) {
        setHostingData(hosting);
        saved['hosting'] = { provider: hosting.hosting_provider };
      }

      // PageSpeed
      const ps = psRes.status === 'fulfilled' ? psRes.value : null;
      if (ps?.mobile_score != null) {
        saved['pagespeed'] = { mobile: ps.mobile_score, desktop: ps.desktop_score };
        setSavedPagespeed(ps);
      }

      // Brand
      const brand = brandRes.status === 'fulfilled' ? brandRes.value : null;
      if (brand?.primary_color) {
        saved['brand'] = { primary: brand.primary_color, fonts: brand.all_fonts?.length || 0 };
        setSavedBrand(brand);
      }

      setStepResults(saved);
      // Notify parent (ProzessFlow) about loaded data for step completion
      if (onDataUpdate) {
        onDataUpdate({
          crawlPages: content.length,
        });
      }
    } catch (error) {
      // Die vier Abfragen melden sich selbst; hier bleibt nur ein Fehler beim
      // Auswerten uebrig — der wuerde die ganze Uebersicht leer lassen.
      reportApiError(error, 'Analyse-Übersicht');
    } finally { setPagesLoading(false); }
  };

  // ── Einzelnen Schritt ausfuehren ────────────────────────────────────────
  const runStep = async (stepIndex) => {
    const step = steps[stepIndex];
    if (!websiteUrl) { toast.error('Keine Website-URL im Projekt'); return; }
    setRunning(true);
    setCurrentStep(stepIndex);
    setStepProgress(0);
    setStepErrors(prev => { const n = { ...prev }; delete n[step.id]; return n; });
    try {
      const result = await step.run((pct) => setStepProgress(pct));
      setStepResults(prev => ({ ...prev, [step.id]: result }));
      toast.success(`${step.label} abgeschlossen`);
    } catch (err) {
      setStepErrors(prev => ({ ...prev, [step.id]: err.message }));
      toast.error(`${step.label} fehlgeschlagen`);
    }
    setCurrentStep(-1);
    setRunning(false);
    // Seitenliste aktualisieren nach Crawler/Content
    if (step.id === 'url-crawl' || step.id === 'content-scrape') {
      const content = await fetch(`${API_BASE_URL}/api/crawler/content/${leadId}`, { headers }).then(r => r.ok ? r.json() : []).catch(() => []);
      setPages(content);
      if (content.length > 0 && !selectedPage) setSelectedPage(content[0]);
    }
    // Panel-Daten aktualisieren nach PageSpeed/Brand
    if (step.id === 'pagespeed') {
      const ps = await fetch(`${API_BASE_URL}/api/leads/${leadId}/pagespeed`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null);
      if (ps) setSavedPagespeed(ps);
    }

    // Crawl-Daten nach oben melden
    if (step.id === 'url-crawl' || step.id === 'content-scrape') {
      if (onDataUpdate) onDataUpdate({ crawlPages: pages.length });
    }
  };

  // ── Alle Schritte ausfuehren ────────────────────────────────────────────
  const runPipeline = async () => {
    if (!websiteUrl) { toast.error('Keine Website-URL im Projekt'); return; }
    setRunning(true);
    setStepResults({});
    setStepErrors({});

    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      setCurrentStep(i);
      setStepProgress(0);
      try {
        const result = await step.run((pct) => setStepProgress(pct));
        setStepResults(prev => ({ ...prev, [step.id]: result }));
      } catch (err) {
        setStepErrors(prev => ({ ...prev, [step.id]: err.message }));
      }
    }

    setCurrentStep(-1);
    setRunning(false);
    // Gespeicherte Ergebnisse nachladen (Seiten-Board + Panel-Daten aktualisieren)
    await loadSavedResults();
    toast.success('Alle Analysen abgeschlossen — Ergebnisse gespeichert!');
  };

  // ── Parallele Vollanalyse ───────────────────────────────────────────────
  const runParallelPipeline = async () => {
    if (!websiteUrl) { toast.error('Keine Website-URL im Projekt'); return; }
    setRunning(true);
    setCurrentStep(-1);
    setStepResults({});
    setStepErrors({});
    setParallelRunning(new Set());

    const stepMap = Object.fromEntries(steps.map(s => [s.id, s]));

    const execStep = async (stepId) => {
      const step = stepMap[stepId];
      if (!step) return null;
      setParallelRunning(prev => new Set([...prev, stepId]));
      try {
        const result = await step.run(() => {});
        setStepResults(prev => ({ ...prev, [stepId]: result }));
        return result;
      } catch (err) {
        setStepErrors(prev => ({ ...prev, [stepId]: err.message }));
        return null;
      } finally {
        setParallelRunning(prev => { const n = new Set(prev); n.delete(stepId); return n; });
      }
    };

    try {
      // Phase 1: url-crawl + hosting + pagespeed — alle gleichzeitig
      const independentIds = steps
        .filter(s => !['content-scrape', 'analytics'].includes(s.id))
        .map(s => s.id);

      const phase1 = await Promise.allSettled(
        independentIds.map(id => execStep(id))
      );

      // Phase 2: content-scrape (braucht url-crawl)
      const crawlIdx = independentIds.indexOf('url-crawl');
      const crawlOk  = crawlIdx >= 0 && phase1[crawlIdx]?.status === 'fulfilled' && phase1[crawlIdx]?.value !== null;

      if (crawlOk) {
        const content = await fetch(
          `${API_BASE_URL}/api/crawler/content/${leadId}`,
          { headers }
        ).then(r => r.ok ? r.json() : []).catch(() => []);
        setPages(content);
        if (content.length > 0 && !selectedPage) setSelectedPage(content[0]);

        if (stepMap['content-scrape']) await execStep('content-scrape');

        // Phase 3: analytics (braucht content-scrape)
        if (stepMap['analytics']) await execStep('analytics');
      }

      await loadSavedResults();
      toast.success('Vollanalyse abgeschlossen — alle Daten gespeichert!', { duration: 4000 });
    } catch (err) {
      toast.error('Vollanalyse unterbrochen');
    } finally {
      setCurrentStep(-1);
      setParallelRunning(new Set());
      setRunning(false);
    }
  };

  const [saveStatus, setSaveStatus] = useState(''); // '', 'saving', 'saved'

  // ── Gesamtfortschritt ────────────────────────────────────────────────────
  const doneCount  = Object.keys(stepResults).length + Object.keys(stepErrors).length;
  const totalPct   = running
    ? Math.round(((doneCount / steps.length) + (stepProgress / 100 / steps.length)) * 100)
    : doneCount === steps.length ? 100 : 0;

  // ── Manuell speichern / verifizieren ─────────────────────────────────────
  const saveResults = async () => {
    setSaveStatus('saving');
    try {
      await loadSavedResults();
      setSaveStatus('saved');
      toast.success('Ergebnisse gespeichert');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch {
      setSaveStatus('');
      toast.error('Speichern fehlgeschlagen');
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
            Analyse-Zentrale
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {websiteUrl || 'Keine URL hinterlegt'}
          </div>
        </div>
        {doneCount > 0 && !running && (
          <button
            onClick={saveResults}
            disabled={saveStatus === 'saving'}
            style={{
              padding: '9px 18px', borderRadius: 8, flexShrink: 0,
              border: saveStatus === 'saved' ? '1px solid var(--status-success-text)' : '1px solid var(--brand-primary, var(--kc-mid))',
              background: saveStatus === 'saved' ? 'var(--status-success-bg)' : 'transparent',
              color: saveStatus === 'saved' ? 'var(--status-success-text)' : 'var(--brand-primary, var(--kc-mid))',
              fontSize: 12, fontWeight: 700,
              cursor: saveStatus === 'saving' ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-sans)',
              display: 'flex', alignItems: 'center', gap: 6,
              transition: 'all .2s',
            }}
          >
            {saveStatus === 'saving' ? (
              <><span style={{ width: 11, height: 11, border: '2px solid var(--border-medium)', borderTopColor: 'var(--brand-primary)', borderRadius: '50%', animation: 'spin .8s linear infinite', display: 'inline-block' }} />Speichert...</>
            ) : saveStatus === 'saved' ? '\u2713 Gespeichert' : 'Ergebnisse speichern'}
          </button>
        )}
      </div>

      {/* ── Vollanalyse-Button ── */}
      <div style={{
        background: running ? 'var(--bg-surface)' : 'linear-gradient(135deg, var(--kc-mid), #006680)',
        border: running ? '1px solid var(--border-light)' : 'none',
        borderRadius: 12,
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        boxShadow: running ? 'none' : '0 4px 14px var(--kc-mid-a-30)',
      }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: running ? 'var(--text-primary)' : '#fff', marginBottom: 3 }}>
            {running ? 'Vollanalyse läuft …' : 'Vollanalyse starten'}
          </div>
          <div style={{ fontSize: 12, color: running ? 'var(--text-secondary)' : 'rgba(255,255,255,.8)' }}>
            {running
              ? `${Object.keys(stepResults).length + Object.keys(stepErrors).length} / ${steps.length} Schritte abgeschlossen`
              : 'Crawler · PageSpeed · Hosting — alles auf einmal, parallel'
            }
          </div>
        </div>
        <button
          onClick={runParallelPipeline}
          disabled={running || !websiteUrl}
          style={{
            padding: '10px 24px', borderRadius: 8, border: 'none',
            background: running ? 'var(--border-light)' : 'rgba(255,255,255,.2)',
            color: running ? 'var(--text-secondary)' : '#fff',
            fontSize: 13, fontWeight: 700,
            cursor: (running || !websiteUrl) ? 'not-allowed' : 'pointer',
            flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6,
            fontFamily: 'var(--font-sans)', transition: 'background .2s',
          }}
        >
          {running ? (
            <>
              <span style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(0,0,0,.15)', borderTopColor: 'var(--text-secondary)', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
              Läuft …
            </>
          ) : '\u25b6 Vollanalyse starten'}
        </button>
      </div>

            {/* ── Gesamt-Fortschrittsbalken ── */}
      {(running || doneCount > 0) && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 6 }}>
            <span>{running ? `Schritt ${doneCount + 1} von ${steps.length}` : `${doneCount} von ${steps.length} Schritten abgeschlossen`}</span>
            <span>{totalPct}%</span>
          </div>
          <div style={{ height: 8, background: 'var(--border-light)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${totalPct}%`,
              background: 'linear-gradient(90deg, var(--kc-mid), #00B4D8)',
              borderRadius: 4,
              transition: 'width 0.4s ease',
            }} />
          </div>
        </div>
      )}

      {/* ── Schritt-Kacheln ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
        {steps.map((step, i) => {
          const isDone         = step.id in stepResults;
          const hasError       = step.id in stepErrors;
          const isSingleActive = currentStep === i;
          const isParallel     = parallelRunning.has(step.id);
          const isActive       = isSingleActive || isParallel;
          const result         = stepResults[step.id];
          const error          = stepErrors[step.id];

          return (
            <div key={step.id} style={{
              borderRadius: 10, padding: '14px 16px',
              border: `1px solid ${isActive ? 'var(--brand-primary-mid, var(--kc-mid))' : isDone ? 'var(--status-success-text)' : hasError ? 'var(--status-danger-text)' : 'var(--border-light)'}`,
              background: isActive ? 'var(--brand-primary-light, #E6F6FA)' : isDone ? 'var(--status-success-bg)' : hasError ? 'var(--status-danger-bg)' : 'var(--bg-surface)',
              transition: 'all 0.3s',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 18 }}>{step.icon}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: isActive ? 'var(--brand-primary-dark, #006680)' : isDone ? 'var(--status-success-text)' : hasError ? 'var(--status-danger-text)' : 'var(--text-primary)' }}>
                  {step.label}
                </span>
                {isActive && <span style={{ marginLeft: 'auto', width: 12, height: 12, border: '2px solid var(--brand-primary, var(--kc-mid))', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin .7s linear infinite', display: 'inline-block', flexShrink: 0 }} />}
                {isDone && !isActive && <span style={{ marginLeft: 'auto', fontSize: 14, color: 'var(--status-success-text)' }}>&#10003;</span>}
                {hasError && !isActive && <span style={{ marginLeft: 'auto', fontSize: 14, color: 'var(--status-danger-text)' }}>&#10007;</span>}
              </div>

              {isSingleActive && (
                <div style={{ height: 4, background: 'var(--brand-primary-light, #E6F6FA)', borderRadius: 2, overflow: 'hidden', marginBottom: 6 }}>
                  <div style={{ height: '100%', width: `${stepProgress}%`, background: 'var(--brand-primary, var(--kc-mid))', borderRadius: 2, transition: 'width 0.4s' }} />
                </div>
              )}

              <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{step.desc}</div>

              {isDone && result && (
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--status-success-text)', fontWeight: 600 }}>
                  {step.id === 'url-crawl'      && `${result.urls} URLs gefunden`}
                  {step.id === 'content-scrape'  && `${result.pages} Seiten gescrapt`}
                  {step.id === 'hosting'         && result.provider}
                  {step.id === 'pagespeed'       && `Mobil: ${result.mobile ?? '—'} / Desktop: ${result.desktop ?? '—'}`}
                  {step.id === 'analytics'       && (result.ga_found ? 'GA4 erkannt' : 'Kein GA4 gefunden')}

                </div>
              )}
              {hasError && (
                <div style={{ marginTop: 6, fontSize: 12, color: 'var(--status-danger-text)' }}>{error}</div>
              )}

              {/* Einzelner Start-Button pro Schritt */}
              {!isActive && !running && (
                <button
                  onClick={() => runStep(i)}
                  disabled={!websiteUrl}
                  style={{
                    marginTop: 10, width: '100%', padding: '6px 0', borderRadius: 6,
                    border: isDone ? '1px solid var(--status-success-text)' : '1px solid var(--brand-primary, var(--kc-mid))',
                    background: isDone ? 'transparent' : 'var(--brand-primary, var(--kc-mid))',
                    color: isDone ? 'var(--status-success-text)' : '#fff',
                    fontSize: 12, fontWeight: 700, cursor: websiteUrl ? 'pointer' : 'not-allowed',
                    fontFamily: 'var(--font-sans)',
                  }}
                >
                  {isDone ? 'Erneut ausfuehren' : 'Starten'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Seiten-Board (Master-Detail) ── */}
      {pages.length > 0 && (
        <div>
          {/* Board-Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
              {pages.length} Seiten analysiert
            </div>
            <input aria-label="URL oder Titel suchen..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="URL oder Titel suchen..."
              style={{
                flex: 1, minWidth: 180, padding: '6px 10px', fontSize: 12,
                border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)',
                background: 'var(--bg-app)', color: 'var(--text-primary)',
                fontFamily: 'var(--font-sans)', outline: 'none',
              }}
            />
            <select aria-label="Sortierung"
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              style={{
                padding: '6px 10px', fontSize: 12,
                border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)',
                background: 'var(--bg-app)', color: 'var(--text-primary)',
                fontFamily: 'var(--font-sans)', cursor: 'pointer',
              }}
            >
              <option value="url">Sortieren: URL</option>
              <option value="words">Sortieren: Woerter</option>
              <option value="images">Sortieren: Bilder</option>
            </select>
          </div>

          {/* Master-Detail Split */}
          {(() => {
            const filtered = pages
              .filter(p => !search || p.url?.toLowerCase().includes(search.toLowerCase()) || p.title?.toLowerCase().includes(search.toLowerCase()) || p.h1?.toLowerCase().includes(search.toLowerCase()))
              .sort((a, b) => {
                if (sortBy === 'words')  return (b.word_count || 0) - (a.word_count || 0);
                if (sortBy === 'images') return ((Array.isArray(b.images) ? b.images.length : 0)) - ((Array.isArray(a.images) ? a.images.length : 0));
                return (a.url || '').localeCompare(b.url || '');
              });

            const sel = selectedPage;

            return (
              <div style={{
                display: 'grid',
                gridTemplateColumns: '280px 1fr',
                gap: 0,
                border: '1px solid var(--border-light)',
                borderRadius: 'var(--radius-lg)',
                height: 680,
              }}>

                {/* LINKE SPALTE */}
                <div style={{
                  borderRight: '1px solid var(--border-light)',
                  background: 'var(--bg-app)',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  borderRadius: 'var(--radius-lg) 0 0 var(--radius-lg)',
                }}>
                  {/* Scrollbare Seiten-Liste */}
                  <div style={{ flex: 1, overflowY: 'auto' }}>
                  {filtered.length === 0 ? (
                    <div style={{ padding: 20, fontSize: 12, color: 'var(--text-tertiary)', textAlign: 'center' }}>
                      Keine Seiten gefunden
                    </div>
                  ) : filtered.map((page, i) => {
                    const isSelected = sel?.url === page.url;
                    const imgCount   = Array.isArray(page.images) ? page.images.length : 0;
                    let path = page.url;
                    try { path = new URL(page.url).pathname || '/'; } catch { /* keep */ }

                    return (
                      <div role="button" tabIndex={0} onKeyDown={aufTaste(() => { setSelectedPage(page); setShowFullText(false); })}
                        key={i}
                        onClick={() => { setSelectedPage(page); setShowFullText(false); }}
                        style={{
                          padding: '10px 14px',
                          borderBottom: '1px solid var(--border-light)',
                          cursor: 'pointer',
                          background: isSelected ? 'var(--bg-active, var(--bg-elevated))' : 'transparent',
                          borderLeft: `3px solid ${isSelected ? 'var(--brand-primary)' : 'transparent'}`,
                          transition: 'background 0.12s',
                        }}
                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--bg-elevated)'; }}
                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <div style={{
                          fontSize: 12, fontWeight: 700,
                          color: isSelected ? 'var(--brand-primary)' : 'var(--text-primary)',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          marginBottom: 3,
                        }}>
                          {path === '/' ? 'Startseite' : path}
                        </div>
                        {(page.title || page.h1) && (
                          <div style={{
                            fontSize: 12, color: 'var(--text-secondary)',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            marginBottom: 5,
                          }}>
                            {page.title || page.h1}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          {page.word_count > 0 && (
                            <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{page.word_count} W</span>
                          )}
                          {imgCount > 0 && (
                            <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{imgCount} Img</span>
                          )}
                          {(Array.isArray(page.h2s) ? page.h2s.length : 0) > 0 && (
                            <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>H2: {page.h2s.length}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  </div>
                  {/* Zusammenfassung */}
                  <ProjectSummaryPanel
                    leadId={leadId}
                    headers={headers}
                    stepResults={stepResults}
                    savedPagespeed={savedPagespeed}
                    savedBrand={savedBrand}
                  />
                </div>

                {/* RECHTES DETAIL */}
                {sel ? (
                  <div style={{ overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>

                    {/* URL */}
                    <div>
                      <DetailLabel>URL</DetailLabel>
                      <a href={sel.url} target="_blank" rel="noreferrer"
                        style={{ fontSize: 12, color: 'var(--brand-primary-mid)', textDecoration: 'none', wordBreak: 'break-all', lineHeight: 1.5 }}>
                        {sel.url}
                      </a>
                    </div>

                    {/* Seitentitel */}
                    {(sel.title || sel.h1) && (
                      <div>
                        <DetailLabel>Seitentitel</DetailLabel>
                        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.4 }}>
                          {sel.title || sel.h1}
                        </div>
                      </div>
                    )}

                    {/* Headings */}
                    <div>
                      <DetailLabel>Headings</DetailLabel>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {sel.h1 && <HeadingRow level="H1" text={sel.h1} color="var(--brand-primary)" indent={0} />}
                        {(Array.isArray(sel.h2s) ? sel.h2s : []).map((h, j) =>
                          <HeadingRow key={`h2${j}`} level="H2" text={h} color="var(--text-primary)" indent={16} />
                        )}
                        {(Array.isArray(sel.h3s) ? sel.h3s : []).map((h, j) =>
                          <HeadingRow key={`h3${j}`} level="H3" text={h} color="var(--text-secondary)" indent={32} />
                        )}
                        {!sel.h1 && !sel.h2s?.length && !sel.h3s?.length && (
                          <span style={{ fontSize: 12, color: 'var(--text-tertiary)', fontStyle: 'italic' }}>Keine Headings</span>
                        )}
                      </div>
                    </div>

                    {/* Meta */}
                    {sel.meta_description && (
                      <div>
                        <DetailLabel>Meta-Description</DetailLabel>
                        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.65, fontStyle: 'italic' }}>
                          {sel.meta_description}
                        </div>
                      </div>
                    )}

                    {/* Volltext */}
                    {sel.full_text && (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                          <DetailLabel style={{ marginBottom: 0 }}>Volltext - {sel.word_count || 0} Woerter</DetailLabel>
                          <button
                            onClick={() => setShowFullText(v => !v)}
                            style={{ fontSize: 12, color: 'var(--brand-primary-mid)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-sans)', padding: 0 }}
                          >
                            {showFullText ? '\u25B2 Weniger' : '\u25BC Volltext anzeigen'}
                          </button>
                        </div>
                        <div style={{
                          fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.75,
                          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                          background: 'var(--bg-app)', borderRadius: 8, padding: '12px 14px',
                          maxHeight: showFullText ? 600 : 100,
                          overflowY: showFullText ? 'auto' : 'hidden',
                          position: 'relative', transition: 'max-height 0.3s',
                        }}>
                          {sel.full_text}
                          {!showFullText && (
                            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 36, background: 'linear-gradient(transparent, var(--bg-app))', borderRadius: '0 0 8px 8px' }} />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Assets */}
                    {(Array.isArray(sel.images) && sel.images.length > 0) && (
                      <div>
                        <DetailLabel>Assets - {sel.images.length} Bilder</DetailLabel>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {sel.images.slice(0, 12).map((src, j) => {
                            const imgSrc = typeof src === 'string' ? src : src?.src || '';
                            return (
                              <div key={j} title={imgSrc} style={{ width: 56, height: 56, borderRadius: 8, border: '1px solid var(--border-light)', overflow: 'hidden', background: 'var(--bg-app)', flexShrink: 0 }}>
                                <img src={imgSrc} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  onError={e => { e.target.parentNode.style.display = 'none'; }} />
                              </div>
                            );
                          })}
                          {sel.images.length > 12 && (
                            <div style={{ width: 56, height: 56, borderRadius: 8, background: 'var(--bg-app)', border: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--text-tertiary)', fontWeight: 700 }}>
                              +{sel.images.length - 12}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Links */}
                    {((Array.isArray(sel.links_internal) && sel.links_internal.length > 0) ||
                      (Array.isArray(sel.links_external) && sel.links_external.length > 0)) && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                        {sel.links_internal?.length > 0 && (
                          <div>
                            <DetailLabel>Interne Links ({sel.links_internal.length})</DetailLabel>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              {sel.links_internal.slice(0, 15).map((link, j) => {
                                let label = link;
                                try { label = new URL(link).pathname || '/'; } catch { /* keep */ }
                                return (
                                  <a key={j} href={link} target="_blank" rel="noreferrer"
                                    style={{ fontSize: 12, color: 'var(--brand-primary-mid)', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', padding: '1px 0' }}>
                                    {label}
                                  </a>
                                );
                              })}
                              {sel.links_internal.length > 15 && (
                                <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>+ {sel.links_internal.length - 15} weitere</span>
                              )}
                            </div>
                          </div>
                        )}
                        {sel.links_external?.length > 0 && (
                          <div>
                            <DetailLabel>Externe Links ({sel.links_external.length})</DetailLabel>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              {sel.links_external.slice(0, 12).map((link, j) => {
                                let label = link;
                                try { label = new URL(link).hostname; } catch { /* keep */ }
                                return (
                                  <a key={j} href={link} target="_blank" rel="noreferrer"
                                    style={{ fontSize: 12, color: 'var(--text-secondary)', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', padding: '1px 0' }}>
                                    {label}
                                  </a>
                                );
                              })}
                              {sel.links_external.length > 12 && (
                                <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>+ {sel.links_external.length - 12} weitere</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
                    Seite aus der Liste auswaehlen
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {pagesLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
          <div style={{ width: 24, height: 24, borderRadius: '50%', border: '2px solid var(--border-light)', borderTopColor: 'var(--brand-primary, var(--kc-mid))', animation: 'spin 0.8s linear infinite' }} />
        </div>
      )}

      {!running && !pagesLoading && pages.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-tertiary)', fontSize: 13 }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🔬</div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Noch keine Analyse gestartet</div>
          <div>Alle Analysen starten um Daten zu erfassen.</div>
        </div>
      )}

      {/* Schritt abschließen — erscheint wenn Analyse-Daten vorhanden */}
      {!running && (pages.length > 0 || doneCount > 0) && onStepConfirmed && stepId && (
        <div style={{ paddingTop: 16, borderTop: '0.5px solid var(--border-light)' }}>
          {confirmedSteps?.[stepId]?.confirmed ? (
            <div style={{
              padding: '10px 14px', background: '#E3F6EF',
              border: '0.5px solid #00875A33', borderRadius: 8,
              fontSize: 12, fontWeight: 700, color: '#00875A',
            }}>
              ✓ Analyse-Zentrale abgeschlossen
            </div>
          ) : (
            <button
              onClick={async () => {
                if (!projectId || confirmingStep) return;
                setConfirmingStep(true);
                try {
                  const res = await fetch(
                    `${API_BASE_URL}/api/projects/${projectId}/confirm-step`,
                    {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', ...headers },
                      body: JSON.stringify({ step_id: stepId }),
                    }
                  );
                  if (!res.ok) throw new Error();
                  toast.success('Analyse-Zentrale abgeschlossen ✓');
                  onStepConfirmed(stepId);
                } catch {
                  toast.error('Fehler beim Speichern');
                } finally {
                  setConfirmingStep(false);
                }
              }}
              disabled={confirmingStep}
              style={{
                width: '100%', padding: '12px',
                background: 'var(--kc-yellow)', color: '#000',
                border: 'none', borderRadius: 8,
                fontSize: 13, fontWeight: 900,
                cursor: confirmingStep ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-sans)',
                textTransform: 'uppercase', letterSpacing: '.05em',
                opacity: confirmingStep ? 0.7 : 1,
              }}
            >
              {confirmingStep ? 'Wird gespeichert…' : '✓ Analyse abschließen & weiter →'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Hilfskomponenten ─────────────────────────────────────────────────────────

